import type { ApiNotification } from "./notificationEntityShape.ts";
import { buildCategoryConnectDeeplink } from "./categoryConnectHub.ts";
import { buildProfileUrl } from "../utils/navigationHelpers.ts";

function numId(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : null;
}

function looseNum(n: ApiNotification, keys: string[]): string | null {
  const rec = n as Record<string, unknown>;
  for (const k of keys) {
    const id = numId(rec[k]);
    if (id) return id;
  }
  return null;
}

export function resolveMaterialQuotationNavigationId(
  n: ApiNotification
): string | null {
  const sq = n.subQuotationPostulation?.subQuotation;
  const fromNested =
    numId(sq?.quotation?.id) ??
    numId(
      (sq as { quotation_id?: unknown } | null | undefined)?.quotation_id
    ) ??
    numId((sq as { quotationId?: unknown } | null | undefined)?.quotationId);
  return (
    numId(n.quotation?.id) ??
    fromNested ??
    looseNum(n, ["quotationId", "idQuotation", "quotation_id"])
  );
}

export function buildNotificationDeeplink(n: ApiNotification): string | null {
  const type = n.type;

  if (type === "CATEGORY_CONNECT_ACTIVITY") {
    return buildCategoryConnectDeeplink(n);
  }

  const quotationId = resolveMaterialQuotationNavigationId(n);

  if (
    type === "QUOTATION_SEND" ||
    type === "QUOTATION_MATERIALS_UPDATED" ||
    type === "MATERIAL_DEMAND_ALERT" ||
    type === "MATERIAL_DEMAND_ALERT_GENERIC" ||
    type === "QUOTATION_ACCEPTED" ||
    type === "QUOTATION_UPDATE"
  ) {
    if (quotationId) return `/cotizaciondemateriales/${quotationId}`;
  }

  const proyectoFromPermiso = numId(
    n.postulacionPermiso?.proyecto?.id ??
      (n.postulacionPermiso as { proyectoId?: number } | undefined)?.proyectoId
  );
  const proyectoFromComment = numId(n.comentario_proyecto?.proyecto?.id);
  const proyectoFromCommentAlt = numId(
    (n.comentario_proyecto as { project?: { id?: number } } | null | undefined)
      ?.project?.id
  );
  const proyectoFlat = numId((n as { proyecto?: { id?: number } }).proyecto?.id);
  const projectId =
    numId(n.project?.id) ??
    proyectoFromPermiso ??
    proyectoFromComment ??
    proyectoFromCommentAlt ??
    proyectoFlat ??
    looseNum(n, ["proyectoId", "projectId", "proyecto_id"]);

  if (
    type === "POSTULACION_SEND" ||
    type === "POSTULACION_ACCEPTED" ||
    type === "POSTULACION_NEGATED" ||
    type === "COMMENT_ENVIADO" ||
    type === "COMMENT_RESPONDIDO" ||
    type === "USER_COMMENT_ENVIADO" ||
    type === "USER_COMMENT_RESPONDIDO" ||
    type === "COMMENT_RECIBIDO"
  ) {
    if (projectId) return `/obrasprivadas/${projectId}`;
  }

  if (type === "PRESUPUESTO_ENVIADO" || type === "PRESUPUESTO_ACEPTADO" || type === "PRESUPUESTO_NEGADO") {
    const subProjectProyectoId = numId(
      n.subProyectoPresupuesto?.subProyecto?.proyecto?.id
    );
    if (subProjectProyectoId) return `/obrasprivadas/${subProjectProyectoId}`;
    if (projectId) return `/obrasprivadas/${projectId}`;
  }

  const jobRequestId = numId(
    n.jobRequest?.id ??
      n.jobPostulation?.jobRequest?.id ??
      (n as { jobRequestId?: number }).jobRequestId
  );
  if (type === "NEW_JOB_APLICATION") {
    const publisherId =
      numId(n.userReceiver?.id) ?? looseNum(n, ["userReceiverId", "receiverUserId"]);
    if (publisherId) {
      const jrNum = jobRequestId ? Number(jobRequestId) : NaN;
      return buildProfileUrl(publisherId, null, "Gestión", {
        section: "avisos-laborales",
        jobRequestId: Number.isFinite(jrNum) ? jrNum : undefined,
      });
    }
    if (jobRequestId) return `/bolsa-trabajo/${jobRequestId}`;
  }

  if (type === "NEW_QUESTION") {
    const pfolio = n.portfolio_question?.portfolio;
    const ownerUserId =
      numId(n.userReceiver?.id) ??
      numId(pfolio?.user?.id) ??
      looseNum(n, ["userReceiverId", "receiverUserId"]);
    if (ownerUserId) {
      return buildProfileUrl(ownerUserId, null, "servicios");
    }
  }

  if (type === "QUESTION_RESPONDIDO") {
    const answererId = numId(n.userSender?.id);
    if (answererId) return `/perfil/${answererId}`;
  }

  const postIdFromComment = (() => {
    const pid = n.comment_post?.post_id;
    if (pid === undefined || pid === null) return null;
    if (typeof pid === "object") return numId((pid as { id?: unknown }).id);
    return numId(pid as string | number);
  })();
  const postId = numId(n.post?.id) ?? postIdFromComment;
  if (
    (type === "POST_LIKE_SEND" ||
      type === "POST_COMMENT_SEND" ||
      type === "POST_COMMENT_LIKE" ||
      type === "POST_COMMENT_RESPONSE") &&
    postId
  ) {
    return `/marketplace/explorar`;
  }

  const senderId = numId(n.userSender?.id);
  if (type === "NEW_PROFILE_VIEW" || type === "USER_NEW_PROFILE_VIEW") {
    if (senderId) return `/perfil/${senderId}`;
  }

  if (type === "NEW_PROFILE_REVIEW") {
    const ratedId =
      numId(n.userReceiver?.id) ?? looseNum(n, ["userReceiverId"]);
    if (ratedId) {
      return buildProfileUrl(ratedId, null, "calificaciones");
    }
  }

  if (type === "NEW_CONTACT_REQUEST" && senderId) {
    return `/perfil/${senderId}`;
  }

  if (type === "NEW_COINCIDENCES" && projectId) {
    return `/obrasprivadas/${projectId}`;
  }

  return null;
}
